import React, { useState } from 'react';
import { Heart, MapPin, Clock, Star, Users, Bookmark, Share2, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const priceLabels = {
  free: 'Free',
  budget: 'Budget',
  'mid-range': 'Mid-Range',
  premium: 'Premium'
};

const ActivityCard = ({ activity, onSaveToggle, variant = 'feed' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaved, setIsSaved] = useState(activity.is_saved || false);
  const [saveAnimating, setSaveAnimating] = useState(false);

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }

    setSaveAnimating(true);
    try {
      const response = await axios.post(
        `${API}/api/activities/${activity.id}/save`,
        {},
        { withCredentials: true }
      );
      setIsSaved(response.data.saved);
      if (onSaveToggle) onSaveToggle(activity.id, response.data.saved);
    } catch (error) {
      console.error('Save error:', error);
    }
    setTimeout(() => setSaveAnimating(false), 400);
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({
          title: activity.title,
          text: activity.summary,
          url: window.location.origin + `/activity/${activity.id}`
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.origin + `/activity/${activity.id}`);
    }
  };

  const handleCardClick = () => {
    navigate(`/activity/${activity.id}`);
  };

  if (variant === 'compact') {
    return (
      <div 
        onClick={handleCardClick}
        className="bg-white rounded-2xl overflow-hidden card-hover cursor-pointer flex"
        data-testid={`activity-card-compact-${activity.id}`}
      >
        <div className="w-24 h-24 flex-shrink-0">
          <img 
            src={activity.images?.[0] || 'https://via.placeholder.com/200'} 
            alt={activity.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-3 flex-1 min-w-0">
          <h4 className="font-semibold text-[#2C302E] text-sm truncate">{activity.title}</h4>
          <p className="text-xs text-[#666B67] flex items-center gap-1 mt-1">
            <MapPin className="w-3 h-3" />
            {activity.city}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`badge ${activity.price_range === 'free' ? 'badge-free' : activity.price_range === 'premium' ? 'badge-premium' : 'badge-secondary'}`}>
              {priceLabels[activity.price_range]}
            </span>
            {activity.avg_rating > 0 && (
              <span className="text-xs flex items-center gap-1 text-[#666B67]">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                {activity.avg_rating}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={handleCardClick}
      className="bg-white rounded-3xl overflow-hidden card-hover cursor-pointer"
      data-testid={`activity-card-${activity.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/5] sm:aspect-[4/3]">
        <img 
          src={activity.images?.[0] || 'https://via.placeholder.com/400'} 
          alt={activity.title}
          className="w-full h-full object-cover"
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 gradient-overlay" />
        
        {/* Top badges */}
        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
          {activity.trending_score > 80 && (
            <span className="badge bg-white/90 text-[#E07A5F]">
              Trending
            </span>
          )}
          {activity.grandparent_friendly && (
            <span className="badge bg-white/90 text-[#81B29A]">
              Grandparent Friendly
            </span>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <button 
            onClick={handleSave}
            className={`w-10 h-10 rounded-full bg-white/90 flex items-center justify-center transition-all duration-200 hover:bg-white ${saveAnimating ? 'heart-beat' : ''}`}
            data-testid={`save-button-${activity.id}`}
          >
            <Heart className={`w-5 h-5 ${isSaved ? 'fill-[#E07A5F] text-[#E07A5F]' : 'text-[#2C302E]'}`} />
          </button>
          <button 
            onClick={handleShare}
            className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center transition-all duration-200 hover:bg-white"
            data-testid={`share-button-${activity.id}`}
          >
            <Share2 className="w-5 h-5 text-[#2C302E]" />
          </button>
        </div>
        
        {/* Bottom content overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <h3 className="text-xl sm:text-2xl font-semibold mb-2 drop-shadow-lg">{activity.title}</h3>
          <p className="text-sm text-white/90 flex items-center gap-1 mb-3">
            <MapPin className="w-4 h-4" />
            {activity.city} • {activity.indoor_outdoor}
          </p>
          
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`badge ${activity.price_range === 'free' ? 'badge-free' : activity.price_range === 'premium' ? 'badge-premium' : 'badge-secondary'} !bg-opacity-90`}>
              {priceLabels[activity.price_range]}
            </span>
            {activity.avg_rating > 0 && (
              <span className="flex items-center gap-1 text-sm">
                <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                {activity.avg_rating} ({activity.review_count})
              </span>
            )}
            <span className="flex items-center gap-1 text-sm text-white/80">
              <Clock className="w-4 h-4" />
              {activity.duration_hours}h
            </span>
          </div>
        </div>
      </div>
      
      {/* Card footer */}
      <div className="p-4">
        <p className="text-[#666B67] text-sm line-clamp-2 mb-3">{activity.summary}</p>
        
        {/* Age suitability */}
        <div className="flex items-center gap-2 text-xs text-[#666B67]">
          <Users className="w-4 h-4" />
          <span>Ages: {activity.age_suitability?.join(', ')}</span>
        </div>
        
        {/* CTA */}
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm font-medium text-[#E07A5F]">View Details</span>
          <ChevronRight className="w-5 h-5 text-[#E07A5F]" />
        </div>
      </div>
    </div>
  );
};

export default ActivityCard;
